import { agentRegisterApplicant } from '@/store/apply/actions';
import { useAgentUniversities } from '@/utilities/hooks/useAgentUniversities';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AgentFormKeyNames, AgentRegistrationSchema, BaseRegistrationFormSchema, IAgentRegistration } from 'types';
import { z } from 'zod';
import { structureProgrammeChoicesObject } from '../../utils/programmeChoicesHelper';
import { initialAgentFormValues, UseApplicationFormReturnType } from './types';

// Extend the base schema with the form-level UI fields. BaseRegistrationFormSchema
// re-declares `programmeChoices` as optional, overriding the length-1 array
// version on AgentRegistrationSchema — the form assembles the final value at
// submit time.
const AgentRegistrationFormSchema = AgentRegistrationSchema.extend(BaseRegistrationFormSchema);

// Infer the type from the schema
export type IAgentRegistrationForm = z.infer<typeof AgentRegistrationFormSchema>;

export function useAgentForm(isOpen: boolean): UseApplicationFormReturnType<IAgentRegistrationForm> {
  const dispatch = useDispatch();

  const form = useForm<IAgentRegistrationForm>({
    resolver: zodResolver(AgentRegistrationFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: initialAgentFormValues,
  });

  const { handleSubmit, reset, setValue } = form;

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const submitAgentRegistration = (values: IAgentRegistrationForm) => {
    const programmeChoices = structureProgrammeChoicesObject({
      courseId: values[AgentFormKeyNames.COURSE_ID],
      courseName: values[AgentFormKeyNames.COURSE_NAME],
      intakeId: values[AgentFormKeyNames.INTAKE_ID],
      studyLevel: values[AgentFormKeyNames.STUDY_LEVEL],
      mode: values[AgentFormKeyNames.MODE],
      campus: values[AgentFormKeyNames.CAMPUS],
      entryPoint: values[AgentFormKeyNames.ENTRY_POINT],
    });

    const registrationData: IAgentRegistration = {
      email: values[AgentFormKeyNames.EMAIL]?.toLowerCase().trim(),
      preferredName: values[AgentFormKeyNames.PREFERRED_NAME],
      universityId: values[AgentFormKeyNames.UNIVERSITY_ID],
      nationality: values[AgentFormKeyNames.NATIONALITY],
      dob: values[AgentFormKeyNames.DOB],
      phoneNumber: values[AgentFormKeyNames.MOBILE_NUMBER],
      feeStatus: values[AgentFormKeyNames.FEE_STATUS],
      agentConsent: values[AgentFormKeyNames.AGENT_CONSENT],
      programmeChoices,
    };

    dispatch(agentRegisterApplicant(registrationData));
  };

  const onSubmit = (values: IAgentRegistrationForm) => {
    submitAgentRegistration(values);
  };

  const clearDependentFields = useCallback(
    (fields: string[]) => {
      for (const field of fields) {
        if (field === 'courseName') {
          setValue(AgentFormKeyNames.COURSE_NAME, '', { shouldValidate: false });
        } else if (field === 'mode') {
          setValue(AgentFormKeyNames.MODE, '', { shouldValidate: false });
        } else if (field === 'campus') {
          setValue(AgentFormKeyNames.CAMPUS, '', { shouldValidate: false });
        } else if (field === 'entryPoint') {
          setValue(AgentFormKeyNames.ENTRY_POINT, '', { shouldValidate: false });
        } else if (field === 'courseId') {
          setValue(AgentFormKeyNames.COURSE_ID, '', { shouldValidate: false });
        } else {
          setValue(field as keyof IAgentRegistrationForm, '', { shouldValidate: false });
        }
      }
    },
    [setValue],
  );

  const { universities: availableUniversities } = useAgentUniversities();

  return {
    form,
    uniOptions: availableUniversities,
    onSubmit: handleSubmit(onSubmit),
    clearDependentFields,
  };
}
